/**
 * `<ts-dropdown-select>` — upgrades a native `<select>` into a keyboard-
 * accessible custom dropdown (trigger button + `role="listbox"` popup).
 * Markup is server-rendered by `snippets/ts-dropdown-select.liquid`; the
 * select stays in the DOM as the no-JS fallback and form value holder
 * (`name`/`ref`/`required` untouched, so `product-form-component` works
 * unchanged). Picking an option sets `select.value` and dispatches `change`.
 *
 * Keyboard: APG listbox pattern — arrows/Home/End (skipping disabled
 * options), type-ahead (space continues a search, so "40 R" is reachable),
 * Enter/Space select, Escape/Tab/outside-click close.
 *
 * Extends HTMLElement, NOT `Component` from `@theme/component`: a Component
 * ancestor claims descendant `ref` attributes and would steal
 * `ref="variantId"` from the enclosing `product-form-component`, breaking
 * add-to-cart. Do not "clean this up".
 */
class TsDropdownSelect extends HTMLElement {
  /** @type {HTMLSelectElement | null} */
  #select = null;

  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {HTMLElement | null} */
  #valueLabel = null;

  /** @type {HTMLElement | null} */
  #listbox = null;

  /** @type {HTMLElement[]} */
  #options = [];

  /** @type {HTMLElement | null} */
  #error = null;

  /** @type {HTMLFormElement | null} */
  #form = null;

  #typeahead = '';

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #typeaheadTimer = undefined;

  #pointerClosing = false;

  connectedCallback() {
    this.#select = this.querySelector('select');
    this.#button = this.querySelector('.ts-dropdown-select__button');
    this.#valueLabel = this.querySelector('.ts-dropdown-select__value');
    this.#listbox = this.querySelector('.ts-dropdown-select__listbox');
    this.#error = this.querySelector('.ts-dropdown-select__error');
    this.#options = Array.from(this.querySelectorAll('[role="option"]'));

    if (!this.#select || !this.#button || !this.#listbox) return;

    this.#button.addEventListener('click', this.#onButtonClick);
    this.#button.addEventListener('keydown', this.#onButtonKeydown);
    this.#button.addEventListener('pointerdown', this.#onButtonPointerDown);
    this.#listbox.addEventListener('click', this.#onListboxClick);
    this.#listbox.addEventListener('keydown', this.#onListboxKeydown);
    this.addEventListener('focusout', this.#onFocusOut);
    this.#select.addEventListener('change', this.#syncFromSelect);

    // Form values restore without a 'change' event; pageshow fires after.
    window.addEventListener('pageshow', this.#syncFromSelect);

    // The <label> targets the hidden select; send its clicks to the button.
    const label = /** @type {HTMLLabelElement | null} */ (
      document.querySelector(`label[for="${CSS.escape(this.#select.id)}"]`)
    );
    label?.addEventListener('click', this.#onLabelClick);

    this.#form = this.closest('form');
    this.#form?.addEventListener('click', this.#guardSubmit, { capture: true });

    this.#syncFromSelect();
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.#onOutsidePointerDown);
    window.removeEventListener('pageshow', this.#syncFromSelect);
    this.#form?.removeEventListener('click', this.#guardSubmit, { capture: true });
  }

  get #isOpen() {
    return this.#button?.getAttribute('aria-expanded') === 'true';
  }

  // Clicking the trigger while open fires focusout (closing the popup) before
  // click; without this flag the click would immediately reopen it.
  #onButtonPointerDown = () => {
    this.#pointerClosing = this.#isOpen;
  };

  /** @param {MouseEvent} event */
  #onButtonClick = (event) => {
    event.preventDefault();
    if (this.#pointerClosing) {
      this.#pointerClosing = false;
      this.#close(false);
    } else if (this.#isOpen) {
      this.#close(true);
    } else {
      this.#open();
    }
  };

  /** @param {KeyboardEvent} event */
  #onButtonKeydown = (event) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        this.#open();
        break;

      case 'Home':
        event.preventDefault();
        this.#open(this.#enabledOptions()[0]);
        break;

      case 'End':
        event.preventDefault();
        {
          const enabled = this.#enabledOptions();
          this.#open(enabled[enabled.length - 1]);
        }
        break;

      case 'Escape':
        if (this.#isOpen) {
          event.preventDefault();
          // Only the dropdown closes — not an enclosing dialog/drawer.
          event.stopPropagation();
          this.#close(true);
        }
        break;

      default: {
        const match = this.#typeaheadMatch(event);
        if (match) {
          event.preventDefault();
          this.#open(match);
        }
      }
    }
  };

  /** @param {MouseEvent} event */
  #onListboxClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const option = event.target.closest('[role="option"]');
    if (option instanceof HTMLElement) this.#selectOption(option);
  };

  /** @param {KeyboardEvent} event */
  #onListboxKeydown = (event) => {
    // Navigation skips disabled options, like the native select.
    const options = this.#enabledOptions();
    const active = event.target instanceof HTMLElement ? event.target.closest('[role="option"]') : null;
    const currentIndex = active instanceof HTMLElement ? options.indexOf(active) : -1;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#focusOption(options[Math.min(currentIndex + 1, options.length - 1)]);
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.#focusOption(options[Math.max(currentIndex - 1, 0)]);
        break;

      case 'Home':
        event.preventDefault();
        this.#focusOption(options[0]);
        break;

      case 'End':
        event.preventDefault();
        this.#focusOption(options[options.length - 1]);
        break;

      case 'Enter':
        event.preventDefault();
        if (active instanceof HTMLElement) this.#selectOption(active);
        break;

      case ' ': {
        event.preventDefault();
        // Space continues an in-progress type-ahead ("40 R"); otherwise activates.
        if (this.#typeahead) {
          const match = this.#typeaheadMatch(event);
          if (match) this.#focusOption(match);
        } else if (active instanceof HTMLElement) {
          this.#selectOption(active);
        }
        break;
      }

      case 'Escape':
        event.preventDefault();
        // Only the dropdown closes — not an enclosing dialog/drawer.
        event.stopPropagation();
        this.#close(true);
        break;

      case 'Tab':
        // Let focus move on naturally; #onFocusOut closes the popup.
        break;

      default: {
        const match = this.#typeaheadMatch(event);
        if (match) {
          event.preventDefault();
          this.#focusOption(match);
        }
      }
    }
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (!this.#isOpen) return;
    const next = event.relatedTarget;
    // Focus back on the trigger (Shift+Tab out of the options) closes too.
    if (next instanceof Node && this.contains(next) && next !== this.#button) return;
    this.#close(false);
  };

  /** @param {PointerEvent} event */
  #onOutsidePointerDown = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#close(false);
  };

  /** @param {MouseEvent} event */
  #onLabelClick = (event) => {
    event.preventDefault();
    this.#button?.focus();
  };

  /**
   * Capture-phase form guard: no value picked → prevent submit clicks and
   * show the inline error (the hidden select can't surface its `required`
   * bubble). Only preventDefault — stopPropagation would swallow document
   * bubble handlers (localization/popover outside-click dismissal).
   * component.js delegates on:click at document capture, so this runs AFTER
   * AddToCartComponent.handleClick — its animation is only skipped because
   * `required` fails checkValidity(). Keep `required`; it also (silently)
   * blocks programmatic requestSubmit().
   * @param {MouseEvent} event
   */
  #guardSubmit = (event) => {
    if (!(event.target instanceof Element)) return;
    const submitter = event.target.closest('button:not([type="button"]), input[type="submit"]');
    if (!submitter) return;

    // Restored/externally-set values must count (and show).
    this.#syncFromSelect();
    if (this.#select?.value) return;

    event.preventDefault();
    this.#showError();
  };

  /**
   * @param {HTMLElement} [focusTarget] - option to focus once open
   */
  #open(focusTarget) {
    if (!this.#button || !this.#listbox) return;

    this.#syncFromSelect();
    this.#listbox.hidden = false;
    this.#button.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', this.#onOutsidePointerDown);

    const selected = this.#options.find((option) => option.getAttribute('aria-selected') === 'true');
    this.#focusOption(focusTarget ?? selected ?? this.#enabledOptions()[0] ?? this.#options[0]);
  }

  /** @param {boolean} returnFocus */
  #close(returnFocus) {
    if (!this.#button || !this.#listbox) return;

    this.#listbox.hidden = true;
    this.#button.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', this.#onOutsidePointerDown);
    this.#clearTypeahead();

    if (returnFocus) this.#button.focus();
  }

  /** @param {HTMLElement} [option] */
  #focusOption(option) {
    if (!option) return;

    for (const other of this.#options) {
      other.tabIndex = other === option ? 0 : -1;
    }
    option.focus();
  }

  /** @param {HTMLElement} option */
  #selectOption(option) {
    if (option.getAttribute('aria-disabled') === 'true') return;
    if (!this.#select) return;

    this.#select.value = option.dataset.value ?? '';
    this.#select.dispatchEvent(new Event('change', { bubbles: true }));
    this.#close(true);
  }

  /** Mirrors the native select's current value onto the custom UI. */
  #syncFromSelect = () => {
    if (!this.#select) return;

    const value = this.#select.value;
    let selectedText = '';

    for (const option of this.#options) {
      const isSelected = value !== '' && option.dataset.value === value;
      option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      if (isSelected) selectedText = option.textContent?.trim() ?? '';
    }

    if (this.#valueLabel) {
      this.#valueLabel.textContent = selectedText || this.#valueLabel.dataset.placeholder || '';
    }
    this.classList.toggle('ts-dropdown-select--has-value', value !== '');

    if (value !== '') this.#hideError();
  };

  #showError() {
    if (this.#error) {
      this.#error.hidden = false;
      this.#button?.setAttribute('aria-describedby', this.#error.id);
    }
    this.#button?.setAttribute('aria-invalid', 'true');
    this.#button?.focus();
  }

  #hideError() {
    if (this.#error) this.#error.hidden = true;
    this.#button?.removeAttribute('aria-describedby');
    this.#button?.removeAttribute('aria-invalid');
  }

  #enabledOptions() {
    return this.#options.filter((option) => option.getAttribute('aria-disabled') !== 'true');
  }

  #clearTypeahead() {
    clearTimeout(this.#typeaheadTimer);
    this.#typeahead = '';
  }

  /**
   * Buffered type-ahead over enabled options ("40" jumps to "40 S"). Space
   * only continues an in-progress search; modified keys are shortcuts, not
   * type-ahead.
   * @param {KeyboardEvent} event
   * @returns {HTMLElement | undefined}
   */
  #typeaheadMatch(event) {
    if (event.ctrlKey || event.metaKey || event.altKey) return undefined;
    const key = event.key;
    if (key.length !== 1) return undefined;
    if (key === ' ' && this.#typeahead === '') return undefined;

    clearTimeout(this.#typeaheadTimer);
    this.#typeahead += key.toLowerCase();
    this.#typeaheadTimer = setTimeout(() => {
      this.#typeahead = '';
    }, 500);

    return this.#enabledOptions().find((option) =>
      (option.textContent?.trim().toLowerCase() ?? '').startsWith(this.#typeahead)
    );
  }
}

if (!customElements.get('ts-dropdown-select')) {
  customElements.define('ts-dropdown-select', TsDropdownSelect);
}
